
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Shield, AlertTriangle, CheckCircle, Activity, Users } from 'lucide-react';
import SecurityAuditLog from '@/components/admin/SecurityAuditLog';

const SecurityDashboard = () => {
  const { profile } = useAuth();
  const { t } = useLanguage();

  if (!profile || !['admin', 'superadmin'].includes(profile.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p className="text-muted-foreground">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">
          {t('security_dashboard', 'لوحة الأمان', 'Security Dashboard')}
        </h1>
        <p className="text-muted-foreground">
          {t('security_dashboard_desc', 'مراقبة وإدارة أمان النظام', 'Monitor and manage system security')}
        </p>
      </div>

      {/* Security Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('security_status', 'حالة الأمان', 'Security Status')}
            </CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="text-2xl font-bold text-green-600">
                {t('secure', 'آمن', 'Secure')}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('all_systems_secure', 'جميع الأنظمة آمنة', 'All systems secure')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('security_alerts', 'التنبيهات الأمنية', 'Security Alerts')}
            </CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">0</div>
            <p className="text-xs text-muted-foreground">
              {t('active_alerts', 'تنبيهات نشطة', 'Active alerts')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('failed_logins_24h', 'محاولات الدخول الفاشلة (24س)', 'Failed Logins (24h)')}
            </CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">0</div>
            <p className="text-xs text-muted-foreground">
              {t('failed_attempts', 'محاولات فاشلة', 'Failed attempts')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('active_sessions', 'الجلسات النشطة', 'Active Sessions')}
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">-</div>
            <p className="text-xs text-muted-foreground">
              {t('current_sessions', 'جلسات حالية', 'Current sessions')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Security Policies Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            {t('security_policies_status', 'حالة السياسات الأمنية', 'Security Policies Status')}
          </CardTitle>
          <CardDescription>
            {t('security_policies_desc', 'حالة تطبيق السياسات الأمنية', 'Current status of security policy implementation')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 border rounded">
                <span className="text-sm font-medium">
                  {t('row_level_security', 'أمان مستوى الصف', 'Row Level Security')}
                </span>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-green-600">
                    {t('enabled', 'مفعل', 'Enabled')}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 border rounded">
                <span className="text-sm font-medium">
                  {t('admin_role_protection', 'حماية دور المدير', 'Admin Role Protection')}
                </span>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-green-600">
                    {t('active', 'نشط', 'Active')}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 border rounded">
                <span className="text-sm font-medium">
                  {t('audit_logging', 'تسجيل المراجعة', 'Audit Logging')}
                </span>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-green-600">
                    {t('active', 'نشط', 'Active')}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 border rounded">
                <span className="text-sm font-medium">
                  {t('password_security', 'أمان كلمة المرور', 'Password Security')}
                </span>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-green-600">
                    {t('enforced', 'مطبق', 'Enforced')}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 border rounded">
                <span className="text-sm font-medium">
                  {t('email_verification', 'التحقق من البريد', 'Email Verification')}
                </span>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-green-600">
                    {t('required', 'مطلوب', 'Required')}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 border rounded">
                <span className="text-sm font-medium">
                  {t('secure_functions', 'الوظائف الآمنة', 'Secure Functions')}
                </span>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-green-600">
                    {t('implemented', 'مطبق', 'Implemented')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Audit Log */}
      <SecurityAuditLog />

      {/* Security Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            {t('security_recommendations', 'التوصيات الأمنية', 'Security Recommendations')}
          </CardTitle>
          <CardDescription>
            {t('recommendations_desc', 'توصيات لتحسين أمان النظام', 'Recommendations to improve system security')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded">
              <CheckCircle className="h-5 w-5 text-blue-500 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800">
                  {t('security_fixes_applied', 'تم تطبيق الإصلاحات الأمنية', 'Security fixes have been applied')}
                </p>
                <p className="text-sm text-blue-700">
                  {t('critical_vulnerabilities_fixed', 'تم إصلاح الثغرات الأمنية الحرجة', 'Critical security vulnerabilities have been fixed')}
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium text-green-800">
                  {t('rls_policies_enabled', 'تم تفعيل سياسات RLS', 'RLS policies have been enabled')}
                </p>
                <p className="text-sm text-green-700">
                  {t('data_access_secured', 'تم تأمين الوصول للبيانات', 'Data access has been secured')}
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">
                  {t('monitor_regularly', 'راقب النظام بانتظام', 'Monitor system regularly')}
                </p>
                <p className="text-sm text-yellow-700">
                  {t('check_audit_logs', 'تحقق من سجلات المراجعة بانتظام', 'Check audit logs regularly for suspicious activity')}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SecurityDashboard;
